import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Building2,
  LayoutDashboard,
  FileText,
  Users,
  LogOut,
  Search,
  Filter,
  AlertTriangle,
  MessageSquare,
  ChevronRight,
  Menu,
  X,
  Clock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const statusColors = {
  recebida: "bg-blue-100 text-blue-800",
  em_analise: "bg-yellow-100 text-yellow-800",
  investigacao: "bg-purple-100 text-purple-800",
  concluida: "bg-green-100 text-green-800",
  arquivada: "bg-slate-100 text-slate-800",
  respondida: "bg-green-100 text-green-800",
  fechada: "bg-slate-100 text-slate-800",
};

const statusLabels = {
  recebida: "Recebida",
  em_analise: "Em Análise",
  investigacao: "Investigação",
  concluida: "Concluída",
  arquivada: "Arquivada",
  respondida: "Respondida",
  fechada: "Fechada",
};

const categoriaLabels = {
  fraude: "Fraude",
  assedio: "Assédio",
  seguranca: "Segurança",
  corrupcao: "Corrupção",
  outro: "Outro",
};

const tipoOuvidoriaLabels = {
  sugestao: "Sugestão",
  reclamacao: "Reclamação",
  elogio: "Elogio",
  informacao: "Informação",
};

const prioridadeColors = {
  alta: "bg-red-100 text-red-800",
  media: "bg-yellow-100 text-yellow-800",
  baixa: "bg-green-100 text-green-800",
};

export default function CasesList() {
  const { user, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [denuncias, setDenuncias] = useState([]);
  const [ouvidorias, setOuvidorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get("tipo") || "denuncia");
  const [filters, setFilters] = useState({
    status: searchParams.get("status") || "",
    categoria: "",
    prioridade: "",
    search: searchParams.get("q") || "",
  });

  useEffect(() => {
    fetchData();
  }, [filters, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (filters.search) {
        const response = await axios.get(`${API_URL}/search?q=${filters.search}`);
        setDenuncias(response.data.denuncias);
        setOuvidorias(response.data.ouvidorias);
      } else {
        const params = new URLSearchParams();
        if (filters.status) params.append("status", filters.status);
        if (filters.categoria) params.append("categoria", filters.categoria);
        if (filters.prioridade) params.append("prioridade", filters.prioridade);

        const [denRes, ouvRes] = await Promise.all([
          axios.get(`${API_URL}/denuncias?${params.toString()}`),
          axios.get(`${API_URL}/ouvidoria?${params.toString()}`),
        ]);
        setDenuncias(denRes.data);
        setOuvidorias(ouvRes.data);
      }
    } catch (error) {
      console.error("Error fetching cases:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const isOverdue = (slaDate) => {
    return new Date(slaDate) < new Date();
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 lg:static ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <Link to="/admin" className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1a1a2e] rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-semibold text-[#1a1a2e]">CONTERP</h1>
                  <p className="text-xs text-slate-500">Compliance</p>
                </div>
              </Link>
              <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            <Link
              to="/admin"
              className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
            >
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </Link>
            <Link
              to="/admin/casos"
              className="flex items-center gap-3 px-4 py-3 text-[#1a1a2e] bg-slate-100 rounded-lg font-medium"
            >
              <FileText className="w-5 h-5" />
              Casos
            </Link>
            {user?.role === "admin" && (
              <Link
                to="/admin/usuarios"
                className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
              >
                <Users className="w-5 h-5" />
                Usuários
              </Link>
            )}
          </nav>

          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#1a1a2e] rounded-full flex items-center justify-center text-white font-medium">
                {user?.nome?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1a1a2e] truncate">{user?.nome}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <Button variant="outline" className="w-full justify-start" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-xl font-semibold text-[#1a1a2e]">Gestão de Casos</h1>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 p-6">
          {/* Filters */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    placeholder="Buscar por protocolo..."
                    className="pl-10"
                    data-testid="search-filter"
                  />
                </div>
              </div>
              <Select
                value={filters.status}
                onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}
              >
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="recebida">Recebida</SelectItem>
                  <SelectItem value="em_analise">Em Análise</SelectItem>
                  <SelectItem value="investigacao">Investigação</SelectItem>
                  <SelectItem value="concluida">Concluída</SelectItem>
                  <SelectItem value="arquivada">Arquivada</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.categoria}
                onValueChange={(v) => setFilters({ ...filters, categoria: v === "all" ? "" : v })}
              >
                <SelectTrigger className="w-40" data-testid="categoria-filter">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="fraude">Fraude</SelectItem>
                  <SelectItem value="assedio">Assédio</SelectItem>
                  <SelectItem value="seguranca">Segurança</SelectItem>
                  <SelectItem value="corrupcao">Corrupção</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.prioridade}
                onValueChange={(v) => setFilters({ ...filters, prioridade: v === "all" ? "" : v })}
              >
                <SelectTrigger className="w-40" data-testid="prioridade-filter">
                  <SelectValue placeholder="Prioridade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="media">Média</SelectItem>
                  <SelectItem value="baixa">Baixa</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="bg-white border border-slate-200">
              <TabsTrigger value="denuncia" className="data-[state=active]:bg-[#1a1a2e] data-[state=active]:text-white" data-testid="tab-denuncias">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Denúncias ({denuncias.length})
              </TabsTrigger>
              <TabsTrigger value="ouvidoria" className="data-[state=active]:bg-[#f5a623] data-[state=active]:text-[#1a1a2e]" data-testid="tab-ouvidoria">
                <MessageSquare className="w-4 h-4 mr-2" />
                Ouvidoria ({ouvidorias.length})
              </TabsTrigger>
            </TabsList>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a2e]"></div>
              </div>
            ) : (
              <>
                {/* Denúncias List */}
                <TabsContent value="denuncia" className="space-y-4">
                  {denuncias.length === 0 ? (
                    <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                      <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">Nenhuma denúncia encontrada</p>
                    </div>
                  ) : (
                    denuncias.map((d) => (
                      <Link
                        key={d.id}
                        to={`/admin/denuncias/${d.id}`}
                        className="block bg-white rounded-xl border border-slate-200 p-6 hover:border-[#f5a623]/50 hover:shadow-md transition-all group"
                        data-testid={`denuncia-${d.protocolo}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-mono font-semibold text-[#1a1a2e]">
                                {d.protocolo}
                              </span>
                              <Badge className={statusColors[d.status]}>
                                {statusLabels[d.status]}
                              </Badge>
                              <Badge className={prioridadeColors[d.prioridade]}>
                                {d.prioridade?.charAt(0).toUpperCase() + d.prioridade?.slice(1)}
                              </Badge>
                              <Badge variant="outline">
                                {categoriaLabels[d.categoria]}
                              </Badge>
                            </div>
                            <p className="text-slate-600 text-sm line-clamp-2 mb-3">
                              {d.descricao}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-slate-500">
                              <span>Registrada em {formatDate(d.created_at)}</span>
                              {d.responsavel_nome && (
                                <span>Responsável: {d.responsavel_nome}</span>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    ))
                  )}
                </TabsContent>

                {/* Ouvidoria List */}
                <TabsContent value="ouvidoria" className="space-y-4">
                  {ouvidorias.length === 0 ? (
                    <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                      <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">Nenhuma solicitação encontrada</p>
                    </div>
                  ) : (
                    ouvidorias.map((o) => (
                      <Link
                        key={o.id}
                        to={`/admin/ouvidoria/${o.id}`}
                        className="block bg-white rounded-xl border border-slate-200 p-6 hover:border-[#f5a623]/50 hover:shadow-md transition-all group"
                        data-testid={`ouvidoria-${o.protocolo}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-mono font-semibold text-[#1a1a2e]">
                                {o.protocolo}
                              </span>
                              <Badge className={statusColors[o.status]}>
                                {statusLabels[o.status]}
                              </Badge>
                              <Badge variant="outline">
                                {tipoOuvidoriaLabels[o.tipo]}
                              </Badge>
                              {isOverdue(o.sla_prazo) && o.status !== "fechada" && o.status !== "respondida" && (
                                <Badge className="bg-red-100 text-red-800">
                                  <Clock className="w-3 h-3 mr-1" />
                                  SLA Vencido
                                </Badge>
                              )}
                            </div>
                            <p className="text-slate-600 text-sm line-clamp-2 mb-3">
                              {o.descricao}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-slate-500">
                              <span>De: {o.nome}</span>
                              <span>Registrada em {formatDate(o.created_at)}</span>
                              <span>SLA: {formatDate(o.sla_prazo)}</span>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    ))
                  )}
                </TabsContent>
              </>
            )}
          </Tabs>
        </div>
      </main>
    </div>
  );
}
